import { useEffect, useState } from 'react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error(`Error scrolling to section with id "${id}":`, error);
    }
  };

  return (
    <section id="hero" className="relative min-h-screen md:min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-slate-800 to-orange-900 pt-20 md:pt-0 pb-0 md:pb-0">
      {/* Background Image Desktop */}
      <div className="absolute inset-0 hidden md:block">
        <img 
          src="https://public.readdy.ai/ai/img_res/5eed9d170450d0fa14adde043ac7b604.jpg"
          alt="Installation professionnelle de fenêtres" 
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Background Image Mobile */}
      <div className="absolute inset-0 block md:hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=modern%20elegant%20window%20installation%20professional%20installer%20working%20on%20beautiful%20contemporary%20energy%20efficient%20windows%20in%20bright%20modern%20home%20interior%20with%20clean%20white%20walls%20natural%20sunlight%20streaming%20through%20large%20windows%20professional%20craftsmanship%20home%20renovation%20portrait%20orientation%20soft%20teal%20blue%20green%20ambient%20lighting%20high%20quality%20window%20replacement%20construction%20expertise%20professional%20service%20clean%20minimalist%20aesthetic&width=720&height=1280&seq=hero-window-mobile-teal-blue-elegant-v3&orientation=portrait"
          alt="Installation de fenêtres professionnelle" 
          className="w-full h-full object-cover object-center"
        />
        {/* Overlay bleu-vert pour mobile */}
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/80 via-blue-900/70 to-teal-900/80"></div>
      </div>

      {/* Overlay sombre renforcé pour faire ressortir le texte - Desktop */}
      <div className="absolute inset-0 hidden md:block bg-gradient-to-b from-blue-900/70 via-blue-800/50 to-blue-900/70"></div>

      {/* Content */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Titre principal amélioré */}
          <h1 className="text-3xl sm:text-5xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-8 leading-tight px-2">
            AccesLeads<br />
            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent break-words">
              Demandes Qualifiées Fenêtres
            </span>
          </h1>
          
          {/* Sous-titre agrandi et plus impactant */}
          <p className="text-lg sm:text-xl md:text-xl lg:text-2xl text-gray-200 mb-6 md:mb-10 max-w-4xl mx-auto leading-relaxed px-4 font-medium">
            Développez votre activité avec des demandes clients vérifiées
          </p>

          {/* Explication simple de ce qu'est un lead */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 md:p-6 border border-white/20 mb-8 md:mb-12 max-w-3xl mx-auto">
            <div className="flex items-start gap-3 text-left">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <i className="ri-lightbulb-line text-white text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-bold text-white mb-2">Qu'est-ce qu'un Lead ?</h3>
                <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                  Un <strong className="text-orange-300">lead</strong> est un client potentiel qui a déjà exprimé son besoin de travaux de fenêtres, volets ou baies vitrées. 
                  Nous vous fournissons ses <strong className="text-orange-300">coordonnées complètes</strong> (nom, téléphone, adresse, détails du projet) 
                  pour que vous puissiez le <strong className="text-orange-300">contacter directement</strong> et obtenir le chantier !
                </p>
              </div>
            </div>
          </div>

          {/* Boutons CTA agrandis */}
          <div className={`flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center mb-8 md:mb-16 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} px-4`}>
            <button 
              onClick={() => scrollToSection('demandes')}
              className="bg-orange-600 text-white px-6 sm:px-10 py-3 sm:py-5 rounded-2xl text-base sm:text-lg lg:text-xl font-bold hover:bg-orange-700 transition-all transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              <i className="ri-search-line text-xl sm:text-2xl"></i>
              <span className="truncate">Voir les Demandes Disponibles</span>
            </button>
            <button 
              onClick={() => scrollToSection('whatsapp-alerts')}
              className="bg-green-600 text-white px-6 sm:px-10 py-3 sm:py-5 rounded-2xl text-base sm:text-lg lg:text-xl font-bold hover:bg-green-700 transition-all border-2 border-green-400 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer transform hover:scale-105 w-full sm:w-auto shadow-xl hover:shadow-2xl"
            >
              <i className="ri-whatsapp-line text-xl sm:text-2xl"></i>
              <span className="truncate">Recevoir les Nouveaux Leads</span>
            </button>
          </div>

          {/* Statistiques agrandies et mieux espacées */}
          <div className={`grid grid-cols-3 gap-3 sm:gap-8 md:gap-12 max-w-3xl mx-auto transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} px-4`}>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 sm:p-8 border border-white/20">
              <div className="text-xl sm:text-4xl md:text-5xl font-bold text-orange-400 mb-1 sm:mb-3">100%</div>
              <div className="text-xs sm:text-base md:text-lg text-gray-200 font-medium">Clients Vérifiés</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 sm:p-8 border border-white/20">
              <div className="text-xl sm:text-4xl md:text-5xl font-bold text-orange-400 mb-1 sm:mb-3">24h</div>
              <div className="text-xs sm:text-base md:text-lg text-gray-200 font-medium">Livraison Max</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 sm:p-8 border border-white/20">
              <div className="text-xl sm:text-4xl md:text-5xl font-bold text-orange-400 mb-1 sm:mb-3">France</div>
              <div className="text-xs sm:text-base md:text-lg text-gray-200 font-medium">Entière</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Hidden on mobile */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
          <i className="ri-arrow-down-line text-3xl text-white/70"></i>
        </div>
      </div>
    </section>
  );
}
